import { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import orderBy from 'lodash.orderby';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { fromSystemFields, omitContactFields, toSystemFields } from '@features/ContactFieldsSettings';
import { VIEW_TYPE, Tab, ContactFormPlacement } from '@constants/surveys';
import { accountActions, alertActions, surveysActions } from '@actions';
import { surveyService } from '@services';
import { handleApiError } from '@helpers/api';
import {
    getSurveyLanguageSet,
    getSurveyTextFields,
    isSurveyTextComplete,
    visibleQuestionLanguages,
} from '@helpers/surveyLanguages';
import { useTranslation, useYupValidationResolver, useHasAccessToAsset, useOpenUpgradeDrawer } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { RIGHTS } from '@constants/user';
import { BILLING_TYPE_MAP } from '@constants/billing';
import { GatedLabel } from '@features/Paywall/GatedLabel';
import PreviewSurvey from '../PreviewSurvey';
import PreviewCustomization from '../PreviewCustomization';
import General from './General';
import Customization from './Customization';
import Questions from './Questions';
import { getPreviousQuestionTitles, isQuestionValid } from './helpers';
import { RadioButtonsWrapper, RadioButtons, Modal, FormWrapper, PreviewWrapper } from './styles';
import { ProductEvent } from '@constants/product-events';
import PostHogManager from '@widgets/posthog-manager';

const EditModal = ({ modal, onClose, onCancel, selectedSurvey, brands, onDelete, account }) => {
    const [tab, setTab] = useState(Tab.General);
    const [availablePlatforms, setAvailablePlatforms] = useState([]);

    const initRef = useRef(0);

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { hasAccessToAssetById } = useHasAccessToAsset();
    const { hasLocationAccess, hasBrandAccess } = usePermissions();
    const isQrOnlyPlan = account?.surveysEnabled === false;
    const openUpgradeDrawer = useOpenUpgradeDrawer();

    const rights = hasAccessToAssetById(selectedSurvey, 'surveys');

    const SurveysSchema = yup.object().shape(
        {
            name: yup.string().required('validation.required'),
            customFields: yup.array().of(
                yup.object().shape({
                    name: yup.string().required('validation.required'),
                }),
            ),
            topText: yup.string().when('topText', {
                is: val => val?.length > 0,
                then: schema => schema.max(42, 'validation.maxLength'),
                otherwise: schema => schema.notRequired(),
            }),
            bottomText: yup.string().when('bottomText', {
                is: val => val?.length > 0,
                then: schema => schema.max(42, 'validation.maxLength'),
                otherwise: schema => schema.notRequired(),
            }),
            backgroundColor: yup.string().when('customizeQrCodeDesign', {
                is: true,
                then: schema => schema.required('validation.required').validateColor('validation.hexFormat'),
                otherwise: schema => schema.notRequired(),
            }),
            fillColor: yup.string().when('customizeQrCodeDesign', {
                is: true,
                then: schema => schema.required('validation.required').validateColor('validation.hexFormat'),
                otherwise: schema => schema.notRequired(),
            }),
            message: yup.string().when('sendSurveyEnabled', {
                is: val => val !== false && val !== null,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.notRequired(),
            }),
            followUpText: yup.string().when('followUpEnable', {
                is: true,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.notRequired(),
            }),
        },
        [
            ['topText', 'topText'],
            ['bottomText', 'bottomText'],
        ],
    );

    const resolver = useYupValidationResolver(SurveysSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            name: '',
            satisfactionSurveyEnabled: false,
            satisfactionSurveyThreshold: 5,
            ratingScaleType: 0,
            aiReviewSuggestionsEnabled: false,
            aiReviewTags: [],
            labels: [],
            reviewPlatforms: [],
            topText: '',
            bottomText: '',
            backgroundColor: '',
            fillColor: '',
            qrLogoUrl: '',
            customizeQrCodeDesign: false,
            sendSurveyEnabled: null,
            emailSubject: '',
            landingPageEnable: false,
            socialMediaEnable: false,
            logoUrl: '',
            links: {},
            showReviews: false,
            message: '',
            followUpEnable: false,
            followUpDelay: 7,
            followUpText: '',
            overSurvey: 0,
            emailEnabled: false,
            emailRequired: false,
            phoneEnabled: true,
            phoneRequired: false,
            nameEnabled: true,
            nameRequired: false,
            contactFormPlacement: ContactFormPlacement.NEGATIVE_SCREEN,
            customFields: [],
            locationId: '',
            brandId: '',
            locationType: LOCATION_TYPE.BRAND,
            brandAccess: false,
            valuesAlreadySetToForm: false,
            mainLocationId: null,
            pages: [{}],
            questions: {},
            customTextsEnabled: false,
            texts: [],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'customFields',
    });

    const brandId = watch('brandId');
    const locationId = watch('locationId');
    const questions = watch('questions');
    const pages = watch('pages');
    const customTextsEnabled = watch('customTextsEnabled');
    const texts = watch('texts');
    const satisfactionSurveyEnabled = watch('satisfactionSurveyEnabled');
    const languageSet = useMemo(() => getSurveyLanguageSet(customTextsEnabled, texts), [customTextsEnabled, texts]);

    const allQuestionsHaveValue = useMemo(
        () =>
            Object.entries(questions).every(([key, langs]) => {
                const previousTitles = getPreviousQuestionTitles(questions, key);
                return visibleQuestionLanguages(langs, languageSet).every(question =>
                    isQuestionValid(question, previousTitles),
                );
            }),
        [questions, languageSet],
    );

    const customWordingComplete = useMemo(() => {
        if (!customTextsEnabled) return true;
        const fields = getSurveyTextFields(satisfactionSurveyEnabled);
        return texts.length > 0 && texts.every(text => isSurveyTextComplete(text, fields));
    }, [customTextsEnabled, texts, satisfactionSurveyEnabled]);

    const aiReviewSuggestionsEnabled = watch('aiReviewSuggestionsEnabled');
    const aiReviewTags = watch('aiReviewTags');
    const aiReviewTagsComplete =
        isQrOnlyPlan || !satisfactionSurveyEnabled || !aiReviewSuggestionsEnabled || (aiReviewTags?.length ?? 0) > 0;

    const allPagesHaveQuestions = useMemo(() => {
        if (!pages || pages.length <= 1) return true;
        const usedPages = new Set(Object.values(questions).map(langs => langs[0]?.page || 0));
        return pages.every((_, i) => usedPages.has(i));
    }, [pages, questions]);

    useEffect(() => {
        // Available review platforms come from one backend source (location = its own; brand = union across
        // the brand's locations). The same query backs save-time validation, so the checkbox list and what
        // we accept can't drift.
        if (!brandId && !locationId) {
            setAvailablePlatforms([]);
            return undefined;
        }
        let active = true;
        surveyService
            .getAvailablePlatforms({ locationId, brandId })
            .then(data => {
                if (active) setAvailablePlatforms(orderBy(data || [], ['isShared', 'type'], ['desc', 'asc']));
            })
            .catch(() => {
                if (active) setAvailablePlatforms([]);
            });
        return () => {
            active = false;
        };
    }, [brandId, locationId]);

    useEffect(() => {
        if (availablePlatforms.length) {
            const reviewPlatforms =
                initRef.current === 0
                    ? selectedSurvey.reviewPlatforms.filter(rP => availablePlatforms.some(aP => aP.type === rP))
                    : availablePlatforms.map(p => p.type);
            setValue('reviewPlatforms', reviewPlatforms);
            initRef.current = 1;
        }
    }, [availablePlatforms]);

    useEffect(() => {
        if (selectedSurvey && modal.open) {
            setValue('name', selectedSurvey.name, { shouldValidate: true });
            const brandLocations = selectedSurvey.brandId
                ? brands.find(b => b.id === selectedSurvey.brandId)?.locations || []
                : [];
            const hasAccess = l => hasLocationAccess(l.id) || hasBrandAccess(selectedSurvey.brandId);
            const customizationLocationId = selectedSurvey.locationId
                ? selectedSurvey.locationId
                : brandLocations.find(hasAccess)?.id || brandLocations[0]?.id;
            setValue('mainLocationId', customizationLocationId, { shouldValidate: true });
            setValue('satisfactionSurveyEnabled', isQrOnlyPlan ? false : selectedSurvey.satisfactionSurveyEnabled);
            setValue('satisfactionSurveyThreshold', selectedSurvey.satisfactionSurveyThreshold ?? 5);
            setValue('ratingScaleType', selectedSurvey.ratingScaleType || 0);
            setValue('aiReviewSuggestionsEnabled', isQrOnlyPlan ? false : !!selectedSurvey.aiReviewSuggestionsEnabled);
            setValue('aiReviewTags', selectedSurvey.aiReviewTags || []);
            setValue('labels', selectedSurvey.labels || []);
            Object.entries(fromSystemFields(selectedSurvey.systemFields)).forEach(([name, value]) =>
                setValue(name, value),
            );
            setValue(
                'contactFormPlacement',
                selectedSurvey.contactFormPlacement ?? ContactFormPlacement.NEGATIVE_SCREEN,
            );
            setValue('customFields', selectedSurvey.customFields || []);

            setValue('topText', selectedSurvey.topText || '', { shouldValidate: true });
            setValue('bottomText', selectedSurvey.bottomText || '', { shouldValidate: true });
            setValue('backgroundColor', selectedSurvey.backgroundColor || '#fff', {
                shouldValidate: true,
            });
            setValue('fillColor', selectedSurvey.fillColor || '#1f4c5c', { shouldValidate: true });
            setValue('customizeQrCodeDesign', selectedSurvey.customizeQrCodeDesign);
            setValue('sendSurveyEnabled', isQrOnlyPlan ? false : (selectedSurvey.sendSurveyEnabled ?? null));
            setValue('emailSubject', selectedSurvey.emailSubject || '');
            setValue('qrLogoUrl', selectedSurvey.qrLogoUrl);
            setValue('message', selectedSurvey.message || '', { shouldValidate: true });
            setValue('followUpEnable', isQrOnlyPlan ? false : selectedSurvey.followUpEnable);
            setValue('followUpDelay', selectedSurvey.followUpDelay || 7);
            setValue('followUpText', selectedSurvey.followUpText || '', { shouldValidate: true });
            setValue('overSurvey', selectedSurvey.overSurvey || 0);
            const pageCount = isQrOnlyPlan ? 1 : selectedSurvey.pageCount || 1;
            setValue(
                'pages',
                Array.from({ length: pageCount }, () => ({})),
            );
            setValue('questions', isQrOnlyPlan ? {} : selectedSurvey.questions || {});
            setValue('customTextsEnabled', !!selectedSurvey.customTextsEnabled);
            setValue('texts', selectedSurvey.texts || []);
        }
    }, [selectedSurvey, setValue, modal.open]);

    const isView = modal.type === VIEW_TYPE.VIEW;
    const isCreate = modal.type === VIEW_TYPE.CREATE;
    const [isCancelling, setIsCancelling] = useState(false);

    const handleCancel = async () => {
        setIsCancelling(true);
        try {
            await onCancel();
        } finally {
            setIsCancelling(false);
        }
    };

    const onTabChange = nextTab => {
        if (isQrOnlyPlan && nextTab === Tab.Questions) {
            openUpgradeDrawer({
                defaultPlan: BILLING_TYPE_MAP.PROFESSIONAL,
                titleKey: 'Paywall.upgradeForCustomQuestions',
                subtitleKey: 'Paywall.upgradeForCustomQuestionsDescription',
            });
            return;
        }
        setTab(nextTab);
    };

    const onSubmit = async () => {
        try {
            const values = getValues();
            const { pages: formPages, ...rest } = values;
            await surveyService.editSurvey({
                ...selectedSurvey,
                ...omitContactFields(rest),
                pageCount: formPages?.length || 1,
                locationId: values.locationId || null,
                brandId: values.brandId || null,
                systemFields: toSystemFields(values),
                message: values.message,
                followUpText: values.followUpText,
            });
        } catch (error) {
            const duplicates = error?.errors?.length ? error.errors.join(', ') : '';
            handleApiError(error, 'alertMessages.editFail', { duplicates });
            return;
        }

        if (isCreate) {
            PostHogManager.capture(ProductEvent.SurveyCreated, { source: 'new' });
        }
        await dispatch(surveysActions.getAll());
        await dispatch(accountActions.get());
        dispatch(alertActions.success(t('alertMessages.editSuccess')));
        onClose();
    };

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={isCreate ? handleCancel : onClose}
            closeDisabled={isCancelling}
            isDirty={isDirty}
            title={t('FeedbackSurveys.modalTitle', { surveyName: selectedSurvey?.name || '' })}
            onPrimaryAction={!rights[RIGHTS.WRITE] ? null : handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={
                isSubmitting ||
                isCancelling ||
                !isValid ||
                (!isCreate && !isDirty) ||
                !allQuestionsHaveValue ||
                !allPagesHaveQuestions ||
                !customWordingComplete ||
                !aiReviewTagsComplete
            }
            onAdditionalAction={isCreate || !rights[RIGHTS.WRITE] ? null : onDelete}
            additionalTextButton={t('buttons.delete')}
        >
            <FormProvider {...{ getValues, setValue, control, watch, fields, append, remove }}>
                <FormWrapper>
                    <div>
                        <RadioButtonsWrapper>
                            <RadioButtons
                                value={tab}
                                onChange={onTabChange}
                                options={[
                                    { value: Tab.General, label: t('FeedbackSurveys.tabSettings') },
                                    {
                                        value: Tab.Questions,
                                        label: isQrOnlyPlan ? (
                                            <GatedLabel>{t('common.questions')}</GatedLabel>
                                        ) : (
                                            t('common.questions')
                                        ),
                                    },
                                    { value: Tab.Customization, label: t('FeedbackSurveys.tabSharing') },
                                ]}
                            />
                        </RadioButtonsWrapper>
                    </div>
                    {tab === Tab.General && (
                        <General
                            isView={isView}
                            brands={brands}
                            selectedSurvey={selectedSurvey}
                            availablePlatforms={availablePlatforms}
                            account={account}
                            isQrOnlyPlan={isQrOnlyPlan}
                        />
                    )}
                    {tab === Tab.Questions && <Questions account={account} isView={isView} />}
                    {tab === Tab.Customization && (
                        <Customization isView={isView} selectedSurvey={selectedSurvey} isQrOnlyPlan={isQrOnlyPlan} />
                    )}
                </FormWrapper>
                <PreviewWrapper>
                    {tab === Tab.General || tab === Tab.Questions ? (
                        <PreviewSurvey tab={tab} account={account} externalId={selectedSurvey?.externalId} />
                    ) : null}
                    {tab === Tab.Customization ? (
                        <PreviewCustomization
                            externalId={selectedSurvey?.externalId}
                            locations={account?.companies}
                            brands={brands}
                        />
                    ) : null}
                </PreviewWrapper>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
